import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { TRACK, PAY } from "@/constants/testIds";
import { Search, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

const statusMap = {
  received: { label: "Received", pct: 20, tone: "bg-[#D4AF37]" },
  quoted: { label: "Quote Sent", pct: 40, tone: "bg-[#D4AF37]" },
  in_progress: { label: "In Progress", pct: 65, tone: "bg-[#0F172A] text-white" },
  ready: { label: "Ready for Pickup", pct: 90, tone: "bg-[#0F172A] text-white" },
  picked_up: { label: "Picked Up", pct: 100, tone: "bg-[#0F172A] text-white" },
  cancelled: { label: "Cancelled", pct: 0, tone: "bg-red-100 text-red-900" },
};

export default function TrackOrder() {
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const lookup = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/track", { params: { code, phone } });
      setOrder(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Not found");
      setOrder(null);
    }
    setLoading(false);
  };

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: order.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment error");
      setPaying(false);
    }
  };

  const s = order ? statusMap[order.status] || statusMap.received : null;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="overline">Track Your Order</div>
      <h1 className="font-serif-display text-4xl md:text-5xl mt-3">Find your booking</h1>
      <p className="mt-4 text-[#4A4A4A]">Enter the booking code we sent you along with the phone number used at booking.</p>

      <div className="mt-10 grid md:grid-cols-3 gap-4">
        <input data-testid={TRACK.codeInput} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
               placeholder="HA-XXXXXX"
               className="border border-[#E2E8F0] focus:border-[#0F172A] bg-white p-4 outline-none uppercase tracking-widest"/>
        <input data-testid={TRACK.phoneInput} value={phone} onChange={(e) => setPhone(e.target.value)}
               placeholder="Phone number"
               className="border border-[#E2E8F0] focus:border-[#0F172A] bg-white p-4 outline-none"/>
        <button data-testid={TRACK.lookupBtn} onClick={lookup} disabled={!code || !phone || loading}
                className="inline-flex items-center justify-center gap-2 bg-[#0F172A] text-[#FFFFFF] text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#B8941F] disabled:opacity-30">
          <Search size={14}/> {loading ? "Searching..." : "Look up"}
        </button>
      </div>

      {order && s && (
        <div className="mt-16 border border-[#E2E8F0] bg-[#F8FAFC] p-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="overline">Booking</div>
              <div className="font-serif-display text-3xl">{order.code}</div>
              <div className="text-sm text-[#4A4A4A] mt-1">
                {order.service_type.toUpperCase()} · {order.location}
              </div>
            </div>
            <div data-testid={TRACK.statusBadge} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] ${s.tone}`}>
              {s.label}
            </div>
          </div>

          <div className="mt-8 h-1 bg-[#E2E8F0]/60 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-[#0F172A]" style={{ width: `${s.pct}%` }}/>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="overline">Deposit</div>
              <div className="mt-2 text-lg">
                {order.deposit_amount_aud
                  ? `A$${order.deposit_amount_aud.toFixed(2)} ${order.deposit_paid ? "· PAID" : "· Awaiting payment"}`
                  : "Awaiting quote"}
              </div>
              {order.deposit_amount_aud && !order.deposit_paid && (
                <button data-testid={PAY.payDepositBtn} onClick={payDeposit} disabled={paying}
                        className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0F172A] text-xs uppercase tracking-[0.25em] hover:bg-[#B8941F]">
                  {paying ? "Redirecting..." : "Pay Deposit"}
                </button>
              )}
            </div>
            <div>
              <div className="overline">Appointment</div>
              <div className="mt-2 text-lg">{order.preferred_date} · {order.preferred_time}</div>
              <div className="text-sm text-[#4A4A4A]">{order.location} shop</div>
            </div>
          </div>

          {order.status === "picked_up" && (
            <div className="mt-10 pt-8 border-t border-[#E2E8F0]">
              <div className="overline">Loved the result?</div>
              <Link to={`/review/${order.code}`} data-testid={TRACK.reviewLink}
                    className="mt-4 inline-flex items-center gap-2 text-sm link-underline text-[#0F172A]">
                <MessageCircle size={16}/> Leave a review
              </Link>
            </div>
          )}

          <div className="mt-10 pt-8 border-t border-[#E2E8F0]">
            <div className="overline">Timeline</div>
            <ol className="mt-4 space-y-3">
              {(order.history || []).map((h, i) => (
                <li key={i} className="flex gap-4 text-sm">
                  <span className="w-2 h-2 rounded-full bg-[#0F172A] mt-1.5"/>
                  <div>
                    <div className="font-medium">{(statusMap[h.status] || {}).label || h.status}</div>
                    <div className="text-[#4A4A4A] text-xs">{new Date(h.at).toLocaleString()}</div>
                    {h.note && <div className="text-[#4A4A4A] mt-1">{h.note}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
